import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material'
import { isTerminalJobStatus, type CareTeamSwapJobStatus } from './CareTeamSwapService'
export interface SwapDiffRow {
  abbr: string
  oldName: string
  newName: string
}

interface ConfirmSwapModalProps {
  open: boolean
  onClose: () => void
  diff: SwapDiffRow[]
  memberCount: number
  onConfirm: () => void
  status: CareTeamSwapJobStatus | null
  isSubmitting: boolean
}

/**
 * Confirms the proposed reassignment, kicks off the async job, and reflects its polled status
 * through to a terminal state. Applying is a no-op once a job is in flight or done.
 */
const ConfirmSwapModal = ({
  open,
  onClose,
  diff,
  memberCount,
  onConfirm,
  status,
  isSubmitting,
}: ConfirmSwapModalProps) => {
  const inFlight = isSubmitting || status === 'pending' || status === 'in-progress'
  const done = isTerminalJobStatus(status ?? undefined)
  const succeeded = status === 'complete' || status === 'skipped'

  return (
    <Dialog open={open} onClose={inFlight ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Confirm reassignment</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity={succeeded ? 'success' : 'error'} data-testid="job-result">
            {succeeded
              ? `Job complete — ${memberCount.toLocaleString()} members reassigned.`
              : 'Reassignment failed. No further changes were applied.'}
          </Alert>
        ) : (
          <>
            <Typography sx={{ color: 'text.secondary', mb: 1 }}>
              This reassigns <b>{memberCount.toLocaleString()}</b> members onto the new team.
            </Typography>
            <Box>
              {diff.map(row => (
                <Box
                  key={row.abbr + row.newName}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 1.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      fontSize: 11,
                      fontWeight: 800,
                      color: 'text.disabled',
                      letterSpacing: 0.4,
                    }}
                  >
                    {row.abbr}
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary', textDecoration: 'line-through' }}
                  >
                    {row.oldName || '—'}
                  </Box>
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    →
                  </Box>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {row.newName}
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography sx={{ mt: 1.75, fontSize: 13, color: 'text.secondary' }}>
              Review each member&apos;s current and proposed licensure in the table before applying
              — the system surfaces issues per row but never blocks.
            </Typography>
            {inFlight && (
              <Box sx={{ mt: 2 }} data-testid="job-in-flight">
                <LinearProgress />
                <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: 13 }}>
                  Processing {memberCount.toLocaleString()} swaps in batches…
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={onClose} data-testid="job-done">
            Done
          </Button>
        ) : (
          <>
            <Button color="inherit" onClick={onClose} disabled={inFlight}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={inFlight || diff.length === 0}
              data-testid="confirm-swap"
            >
              Start reassignment
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmSwapModal
